'use client';

import React, { startTransition, useEffect, useState } from 'react';
import { cubicBezier } from 'motion';
import { AnimatePresence, motion } from 'motion/react';

import { cn } from '@/lib/utils';
import { Loader } from '@/components/ui/loader';
import { getLoaderCopy, localizeLoaderMessage } from '@/i18n/loader-copy';
import { useLocale } from 'next-intl';
import { useAuthHandoffHold } from '@/lib/auth/auth-handoff';

type LoaderIntent = React.ComponentProps<typeof Loader>['intent'];

const easeOut = cubicBezier(0.16, 1, 0.3, 1);
const easeIn = cubicBezier(0.4, 0, 1, 1);

const messageVariants = {
  initial: { opacity: 0, filter: 'blur(8px)', y: 6 },
  animate: {
    opacity: 1,
    filter: 'blur(0px)',
    y: 0,
    transition: { duration: 0.28, ease: easeOut },
  },
  exit: {
    opacity: 0,
    filter: 'blur(8px)',
    y: -6,
    transition: { duration: 0.2, ease: easeIn },
  },
};

interface MinimalFullscreenLoaderProps {
  message?: string;
  intent?: LoaderIntent;
  className?: string;
}

const FullscreenLoaderRow = ({
  message = 'In motion',
  intent = 'primary',
  className,
}: MinimalFullscreenLoaderProps) => {
  const [display, setDisplay] = useState<{ message: string; id: number }>({
    message,
    id: 0,
  });

  useEffect(() => {
    startTransition(() => {
      setDisplay((prev) =>
        message === prev.message
          ? prev
          : {
              message,
              id: prev.id + 1,
            }
      );
    });
  }, [message]);

  return (
    <div
      className={cn(
        'bg-background flex min-h-dvh w-full items-center justify-center px-6',
        className
      )}
    >
      <div className="text-foreground flex flex-col items-center gap-3 text-base font-medium">
        <Loader intent={intent} size="md" />
        <div
          className="relative w-[calc(100vw-3rem)] max-w-md"
          aria-live="polite"
          aria-atomic="true"
        >
          <AnimatePresence mode="wait" initial={false}>
            <motion.span
              key={display.id}
              className="block text-center text-balance break-words"
              variants={messageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
            >
              {display.message}
            </motion.span>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

// Simple loader without fullscreen container
export function MinimalLoader({
  message,
  intent = 'primary',
  className,
}: MinimalFullscreenLoaderProps) {
  const copy = getLoaderCopy(useLocale());
  const resolvedMessage = message ?? copy.inMotion;
  const [display, setDisplay] = useState<{ message: string; id: number }>({
    message: resolvedMessage,
    id: 0,
  });

  useEffect(() => {
    startTransition(() => {
      setDisplay((prev) =>
        resolvedMessage === prev.message
          ? prev
          : {
              message: resolvedMessage,
              id: prev.id + 1,
            }
      );
    });
  }, [resolvedMessage]);

  return (
    <div
      className={cn(
        'text-foreground flex flex-col items-center gap-3 text-base font-medium',
        className
      )}
    >
      <Loader intent={intent} size="md" />
      <div
        className="relative w-[calc(100vw-3rem)] max-w-md"
        aria-live="polite"
        aria-atomic="true"
      >
        <AnimatePresence mode="wait" initial={false}>
          <motion.span
            key={display.id}
            className="block text-center text-balance break-words"
            variants={messageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
          >
            {display.message}
          </motion.span>
        </AnimatePresence>
      </div>
    </div>
  );
}

// Full screen loading component for auth and page transitions
export function FullScreenLoader({
  message,
  description: _description,
  className,
}: {
  message?: string;
  description?: string;
  className?: string;
}) {
  const locale = useLocale();
  // During a sign-in handoff this sits under the veil instead of showing as a
  // second loader (#5559).
  useAuthHandoffHold();
  return (
    <div
      className={cn(
        'bg-background flex min-h-dvh w-full items-center justify-center px-6',
        className
      )}
    >
      <MinimalLoader
        message={localizeLoaderMessage(locale, message)}
        intent="primary"
      />
    </div>
  );
}

// Simple icon-based loading component
export function TextLoader({
  message,
  className,
}: {
  message?: string;
  className?: string;
}) {
  const copy = getLoaderCopy(useLocale());
  return (
    <FullscreenLoaderRow
      message={message ?? copy.inMotion}
      className={className}
    />
  );
}

// Inline spinner component for buttons and small areas
export function Spinner({
  size = 'default',
  className,
}: {
  size?: 'sm' | 'default' | 'lg';
  className?: string;
}) {
  const loaderSize = size === 'sm' ? 'sm' : size === 'lg' ? 'lg' : 'md';
  return <Loader size={loaderSize} className={className} />;
}

// Button loading state component
export function ButtonLoader({
  children,
  isLoading,
  loadingText,
  className,
}: {
  children: React.ReactNode;
  isLoading: boolean;
  loadingText?: string;
  className?: string;
}) {
  return (
    <div className={cn('flex items-center gap-2', className)}>
      {isLoading && <Loader size="sm" />}
      {isLoading ? loadingText : children}
    </div>
  );
}

// Simple spinner-only component for minimal loading states
export function SpinnerOnly({
  size = 'default',
  className,
}: {
  size?: 'sm' | 'default' | 'lg';
  className?: string;
}) {
  const loaderSize = size === 'sm' ? 'sm' : size === 'lg' ? 'lg' : 'md';
  return (
    <div
      className={cn('flex min-h-dvh items-center justify-center', className)}
    >
      <Loader intent="primary" size={loaderSize} />
    </div>
  );
}

// Content loading skeleton component
export function ContentLoader({
  lines = 3,
  className,
}: {
  lines?: number;
  className?: string;
}) {
  return (
    <div className={cn('space-y-3', className)}>
      {Array.from({ length: lines }).map((_, i) => {
        // Use index-based pseudo-random width to avoid calling Math.random() during render
        const widthPercent = 60 + ((i * 37) % 40); // Generates values between 60-100% deterministically
        return (
          <div
            key={i}
            className="bg-muted h-4 animate-pulse rounded"
            style={{ width: `${widthPercent}%` }}
          />
        );
      })}
    </div>
  );
}

// Card loading skeleton
export function CardLoader({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        'bg-card border-border space-y-4 rounded-[2.5rem] border-2 p-6',
        className
      )}
    >
      <div className="bg-muted h-6 w-1/3 animate-pulse rounded" />
      <ContentLoader lines={4} />
    </div>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('bg-muted animate-pulse rounded-md', className)} />;
}

// Enhanced loading component with smooth animation
export function TetrisPageLoader({
  message,
  description: _description,
  className,
}: {
  message?: string;
  description?: string;
  className?: string;
}) {
  const copy = getLoaderCopy(useLocale());
  return (
    <FullscreenLoaderRow
      message={message ?? copy.inMotion}
      className={className}
    />
  );
}

// AI-themed loading component with Tetris (kept for AI generation contexts only)
export function AIPageLoader({
  message,
  description: _description,
  className,
}: {
  message?: string;
  description?: string;
  className?: string;
}) {
  const copy = getLoaderCopy(useLocale());
  return (
    <FullscreenLoaderRow
      message={message ?? copy.ideasForming}
      className={className}
      intent="primary"
    />
  );
}

// Design-specific loading component with smooth animation
export function DashboardPageLoader({
  message,
  description: _description,
  className,
}: {
  message?: string;
  description?: string;
  className?: string;
}) {
  const copy = getLoaderCopy(useLocale());
  return (
    <FullScreenLoader
      message={message ?? copy.shapingWorkspace}
      className={className}
    />
  );
}

// Settings page loading component with smooth animation
export function SettingsPageLoader({
  message,
  description: _description,
  className,
}: {
  message?: string;
  description?: string;
  className?: string;
}) {
  const copy = getLoaderCopy(useLocale());
  return (
    <FullScreenLoader
      message={message ?? copy.tuningSettings}
      className={className}
    />
  );
}

// Authentication loading component with smooth animation
export function AuthPageLoader({
  message,
  description: _description,
  className,
}: {
  message?: string;
  description?: string;
  className?: string;
}) {
  const copy = getLoaderCopy(useLocale());
  return (
    <FullScreenLoader
      message={message ?? copy.openingWorkspace}
      className={className}
    />
  );
}

// Studio-specific loading component
export function StudioPageLoader({
  message,
  className,
}: {
  message?: string;
  className?: string;
}) {
  const copy = getLoaderCopy(useLocale());
  return (
    <FullScreenLoader
      message={message ?? copy.startingStudio}
      className={className}
    />
  );
}

export function StudioMinimalLoader({
  message,
  className,
}: {
  message?: string;
  className?: string;
}) {
  const copy = getLoaderCopy(useLocale());
  return (
    <MinimalLoader
      message={message ?? copy.startingStudio}
      className={className}
    />
  );
}
